import { isUuid } from './uuid';

export const HANDLE_MIN_LENGTH = 3;
export const HANDLE_MAX_LENGTH = 30;
export const HANDLE_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const RESERVED_HANDLES = new Set([
  'new',
  'me',
  'edit',
  'settings',
  'admin',
  'api',
  'auth',
  'login',
  'logout',
  'signup',
  'u',
  'l',
  's',
  'g',
  'p',
  'en',
  'ar',
  'www',
  'soulwise',
  'help',
  'support',
  'about',
  'preview',
  'practitioners',
  'seekers',
]);

export const normalizeHandle = value => {
  if (value == null) {
    return '';
  }
  return String(value)
    .trim()
    .toLowerCase();
};

/**
 * @param {string} value
 * @returns {null|'too_short'|'too_long'|'invalid'|'reserved'}
 */
export const profileHandleError = value => {
  const handle = normalizeHandle(value);
  if (!handle) {
    return null;
  }
  if (isUuid(handle) || RESERVED_HANDLES.has(handle)) {
    return 'reserved';
  }
  if (!HANDLE_RE.test(handle)) {
    return 'invalid';
  }
  if (handle.length < HANDLE_MIN_LENGTH) {
    return 'too_short';
  }
  if (handle.length > HANDLE_MAX_LENGTH) {
    return 'too_long';
  }
  return null;
};

export const getProfilePageId = user => {
  const handle = normalizeHandle(user?.attributes?.profile?.publicData?.handle);
  if (handle && !profileHandleError(handle)) {
    return handle;
  }
  return user?.id?.uuid;
};

/** Overlay a resolved vanity handle onto a user entity for share URLs and links. */
export const applyProfileHandle = (user, handle) => {
  const normalized = normalizeHandle(handle);
  if (!user || !normalized || profileHandleError(normalized)) {
    return user;
  }
  const profile = user.attributes?.profile || {};
  const publicData = profile.publicData || {};
  if (publicData.handle === normalized) {
    return user;
  }
  return {
    ...user,
    attributes: {
      ...user.attributes,
      profile: {
        ...profile,
        publicData: {
          ...publicData,
          handle: normalized,
        },
      },
    },
  };
};

/** Apply own-profile and API-resolved handles so listing author links use /u/:handle. */
export const listingAuthorWithHandle = (author, { currentUser, resolvedHandle } = {}) => {
  const ownHandle =
    currentUser?.id?.uuid && author?.id?.uuid === currentUser.id.uuid
      ? currentUser?.attributes?.profile?.publicData?.handle
      : null;
  return applyProfileHandle(applyProfileHandle(author, ownHandle), resolvedHandle);
};

/** Locale-optional public profile path used in share URLs, e.g. `/u/wins`. */
export const profileSharePath = user => {
  const id = getProfilePageId(user);
  return id ? `/u/${id}` : '';
};

const DEFAULT_HANDLE_HOST = 'soulwise.io';

/**
 * Fixed prefix shown before the slug input, e.g. `soulwise.io/u/`.
 * Uses the marketplace host so local/dev URLs stay accurate.
 */
export const profileHandleUrlPrefix = marketplaceRootURL => {
  let host = DEFAULT_HANDLE_HOST;
  if (marketplaceRootURL) {
    try {
      const url = /:\/\//.test(marketplaceRootURL)
        ? new URL(marketplaceRootURL)
        : new URL(`https://${marketplaceRootURL}`);
      if (url.host) {
        host = url.host.replace(/^www\./, '');
      }
    } catch (e) {
      host = DEFAULT_HANDLE_HOST;
    }
  }
  return `${host}/u/`;
};

const slugifyHandleSource = value => {
  if (value == null) {
    return '';
  }
  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, HANDLE_MAX_LENGTH)
    .replace(/-+$/g, '');
};

/**
 * Prefill a profile slug from first name, then display name.
 * Returns '' when neither source can become a valid handle.
 */
export const suggestProfileHandle = ({ firstName, displayName } = {}) => {
  const sources = [firstName, displayName];
  for (let i = 0; i < sources.length; i += 1) {
    const candidate = slugifyHandleSource(sources[i]);
    if (candidate && !profileHandleError(candidate)) {
      return candidate;
    }
  }
  return '';
};

const HANDLE_API_CODES = new Set([
  'too_short',
  'too_long',
  'invalid',
  'reserved',
  'taken',
  'database_unavailable',
]);

/** Map API / network errors to ProfileSettingsForm.handleError_* keys. */
export const profileHandleApiErrorCode = error => {
  const code = error?.data?.code || error?.code;
  if (error?.status === 409 || code === '23505' || code === 'taken') {
    return 'taken';
  }
  if (HANDLE_API_CODES.has(code)) {
    return code;
  }
  return 'failed';
};
